/**
 * AI Command Parser
 * A browser-based keyword parser that transforms natural language commands
 * into structured image-studio operation updates instantly without latency.
 */

export const executeAICommand = (prompt, onUpdate, imageSize, currentResizeOperation) => {
  if (!prompt || typeof prompt !== 'string') return null;
  const lowerPrompt = prompt.toLowerCase().trim();

  // Helper to trigger resize
  const triggerResize = (rw, rh, label) => {
    const currentWidth = currentResizeOperation?.width || imageSize?.width || 1920;
    const targetHeight = Math.round((currentWidth * rh) / rw);
    onUpdate('resize', {
      width: currentWidth,
      height: targetHeight,
      maintainAspectRatio: true,
      ratio: `${rw}:${rh}`,
      enabled: true
    });
    return { message: `Resized to ${label} aspect ratio` };
  };

  // Direct action from interactive buttons
  if (prompt.startsWith('__ACTION__:')) {
    const action = prompt.replace('__ACTION__:', '');
    if (action.startsWith('resize:')) {
      const parts = action.split(':')[1].split('x');
      return triggerResize(parseInt(parts[0]), parseInt(parts[1]), `${parts[0]}:${parts[1]}`);
    }
    if (action.startsWith('effect:')) {
      const effectName = action.split(':')[1];
      onUpdate('effect', { effect: effectName, enabled: true });
      return { message: `Applied ${effectName} effect` };
    }
    if (action.startsWith('mood:')) {
      const moodName = action.split(':')[1];
      onUpdate('effect', { mood: moodName, enabled: true });
      return { message: `Applied ${moodName} mood` };
    }
  }

  // 1. Social Media Contexts
  if (lowerPrompt.includes('youtube') || lowerPrompt.includes('yt thumbnail')) {
    return triggerResize(16, 9, 'YouTube Thumbnail (16:9)');
  }

  if (lowerPrompt.includes('instagram') || lowerPrompt.includes('insta')) {
    if (lowerPrompt.includes('portrait') || lowerPrompt.includes('story') || lowerPrompt.includes('reel')) {
      return triggerResize(9, 16, 'Instagram Story/Reel (9:16)');
    } else {
      return triggerResize(1, 1, 'Instagram Square (1:1)');
    }
  }

  // 2. Direct Aspect Ratios ("resize to 16:9")
  const ratioMatch = lowerPrompt.match(/(\d+):(\d+)/);
  if (ratioMatch) {
    return triggerResize(parseInt(ratioMatch[1], 10), parseInt(ratioMatch[2], 10), `${ratioMatch[1]}:${ratioMatch[2]}`);
  }

  // 3. Ambiguous "resize" or "crop"
  if (lowerPrompt === 'resize' || lowerPrompt === 'crop' || lowerPrompt.startsWith('resize to') || lowerPrompt.startsWith('crop to')) {
    return {
      message: "What size would you like to use?",
      actions: [
        { label: "16:9 (Video)", payload: "__ACTION__:resize:16x9" },
        { label: "9:16 (Story)", payload: "__ACTION__:resize:9x16" },
        { label: "1:1 (Square)", payload: "__ACTION__:resize:1x1" },
        { label: "4:3 (Photo)", payload: "__ACTION__:resize:4x3" }
      ]
    };
  }

  // 4. Ambiguous "effect" or "filter"
  if (lowerPrompt === 'effect' || lowerPrompt === 'filter' || lowerPrompt === 'apply effect') {
    return {
      message: "Which effect or mood would you like?",
      actions: [
        { label: "Cyberpunk", payload: "__ACTION__:mood:cyberpunk" },
        { label: "Cinematic", payload: "__ACTION__:mood:cinematic" },
        { label: "Vintage", payload: "__ACTION__:mood:vintage" },
        { label: "Grayscale", payload: "__ACTION__:effect:grayscale" }
      ]
    };
  }

  // 5. Effects & Moods
  const moods = ['cinematic', 'vintage', 'cyberpunk', 'golden', 'dramatic', 'noir'];
  for (let mood of moods) {
    if (lowerPrompt.includes(mood)) {
      onUpdate('effect', { mood: mood, enabled: true });
      return { message: `Applied ${mood} atmospheric mood` };
    }
  }

  const legacyEffects = ['blur', 'grayscale', 'sepia', 'sharpen'];
  for (let effect of legacyEffects) {
    if (lowerPrompt.includes(effect)) {
      onUpdate('effect', { effect: effect, enabled: true });
      return { message: `Applied ${effect} effect` };
    }
  }

  if (lowerPrompt.includes('brightness') || lowerPrompt.includes('brighten')) {
    onUpdate('effect', { brightness: 0.2, enabled: true });
    return { message: `Increased brightness` };
  }

  if (lowerPrompt.includes('contrast')) {
    onUpdate('effect', { contrast: 0.2, enabled: true });
    return { message: `Increased contrast` };
  }

  // 6. Fallback
  return {
    message: "I can help with that. What do you want to do?",
    actions: [
      { label: "Resize Image", payload: "resize" },
      { label: "Add Effect", payload: "effect" }
    ]
  };
};
